import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../auth';
import { SecureVideoPlayer } from '../../../components/SecureVideoPlayer';
import { getReferencePackLicenseLabel } from '../../legal/referencePackLicenseLabels';
import { getReferencePackThumbnailUrl } from '../../reference-packs/referencePackImages';
import { authHeaders } from '../../../shared/authHeaders';
import { formatShortDateTime } from '../../../shared/dates';
import { formatMoneyCompact } from '../../../shared/money';

type PackStatus = 'draft' | 'ready';
type PackAccessType = 'sellable' | 'free' | 'private';

interface AdminReferencePackSummary {
  id: string;
  title: string;
  description: string | null;
  accessType: PackAccessType;
  productVisibility: string;
  showOnProfile: boolean;
  attendeePriceCents: number | null;
  priceCents: number | null;
  currency: string;
  license: string;
  status: PackStatus;
  coverImageUrl: string | null;
  accessExpiresOn: string | null;
  requiresBuyerApproval: boolean;
  updatedAt: string;
  room: {
    code: string;
    name: string;
    scheduledAt: string | null;
  };
  sellerProfile: {
    id: string | null;
    displayName: string | null;
    username: string | null;
  };
  imageCount: number;
  videoCount: number;
  completedPurchaseCount: number;
  totalRevenueCents: number;
}

interface AdminReferencePackAsset {
  id: string;
  imageUrl: string;
  thumbnailUrl?: string | null;
  capturedAt: string;
  width: number | null;
  height: number | null;
  format: string | null;
  fileSizeBytes: number | null;
  poseName: string | null;
  poseIndex: number | null;
  curationStatus: string;
}

interface AdminReferencePackRecording {
  id: string;
  status: string;
  releaseStatus: string;
  hasVideo: boolean;
  contentType: string | null;
  fileSizeBytes: number | null;
  durationSeconds: number | null;
  startedAt: string | null;
}

interface AdminReferencePackDetail {
  pack: AdminReferencePackSummary;
  assets: AdminReferencePackAsset[];
  recordings: AdminReferencePackRecording[];
}

function formatMoney(cents: number | null, currency = 'USD') {
  if (cents === null) return '-';
  if (cents === 0) return 'Free';
  return formatMoneyCompact(cents, currency);
}

function formatDate(value: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatFileSize(bytes: number | null) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getLicenseLabel(license: string) {
  return getReferencePackLicenseLabel(license) ?? license.replaceAll('_', ' ');
}

function getPoseLabel(asset: AdminReferencePackAsset) {
  if (asset.poseName?.trim()) return asset.poseName;
  if (asset.poseIndex !== null && asset.poseIndex !== undefined) return `Pose ${asset.poseIndex + 1}`;
  return 'Unlabeled';
}

export function ReferencePacksPage() {
  const { getToken } = useAuth();
  const [packs, setPacks] = useState<AdminReferencePackSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminReferencePackDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PackStatus | ''>('');
  const [accessTypeFilter, setAccessTypeFilter] = useState<PackAccessType | ''>('');
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    async function fetchPacks() {
      setIsLoading(true);
      setError(null);

      try {
        const token = await getToken();
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (statusFilter) params.set('status', statusFilter);
        if (accessTypeFilter) params.set('accessType', accessTypeFilter);
        params.set('limit', String(limit));
        params.set('offset', String(page * limit));

        const response = await fetch(`/api/admin/reference-packs?${params}`, {
          headers: authHeaders(token),
        });

        if (!response.ok) {
          throw new Error('Failed to load reference packs');
        }

        const data = await response.json();
        setPacks(data.packs);
        setTotal(data.total);
        setSelectedPackId((current) => {
          if (current && data.packs.some((pack: AdminReferencePackSummary) => pack.id === current)) return current;
          return data.packs[0]?.id ?? null;
        });
      } catch (err) {
        console.error('Error loading reference packs:', err);
        setError(err instanceof Error ? err.message : 'Failed to load reference packs');
      } finally {
        setIsLoading(false);
      }
    }

    void fetchPacks();
  }, [getToken, search, statusFilter, accessTypeFilter, page]);

  useEffect(() => {
    if (!selectedPackId) {
      setDetail(null);
      return;
    }

    async function fetchDetail() {
      setIsDetailLoading(true);

      try {
        const token = await getToken();
        const response = await fetch(`/api/admin/reference-packs/${selectedPackId}`, {
          headers: authHeaders(token),
        });

        if (!response.ok) {
          throw new Error('Failed to load reference pack details');
        }

        setDetail(await response.json());
      } catch (err) {
        console.error('Error loading reference pack detail:', err);
        setDetail(null);
      } finally {
        setIsDetailLoading(false);
      }
    }

    void fetchDetail();
  }, [getToken, selectedPackId]);

  const selectedPack = useMemo(
    () => packs.find((pack) => pack.id === selectedPackId) ?? detail?.pack ?? null,
    [packs, selectedPackId, detail]
  );
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-display font-medium text-text">Reference Packs</h1>
          <p className="text-sm text-text-dim mt-1">
            {total} packs for sale, review, or private access
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-[240px] max-w-md">
          <input
            type="text"
            placeholder="Search by pack, session, code, or model..."
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            className="input w-full"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value as PackStatus | '');
            setPage(0);
          }}
          className="input w-auto"
        >
          <option value="">All statuses</option>
          <option value="ready">Ready</option>
          <option value="draft">Draft</option>
        </select>
        <select
          value={accessTypeFilter}
          onChange={(event) => {
            setAccessTypeFilter(event.target.value as PackAccessType | '');
            setPage(0);
          }}
          className="input w-auto"
        >
          <option value="">All access</option>
          <option value="sellable">Sellable</option>
          <option value="free">Free</option>
          <option value="private">Private</option>
        </select>
      </div>

      {error && (
        <div className="mb-4 border border-red-500/30 bg-red-500/10 text-red-600 rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-text-dim">Loading...</div>
        </div>
      ) : packs.length === 0 ? (
        <div className="bg-panel rounded-xl border border-line p-8 text-center">
          <div className="text-text-dim">No reference packs found</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[420px_minmax(0,1fr)] gap-5">
          <div className="bg-panel rounded-xl border border-line overflow-hidden">
            <div className="max-h-[calc(100vh-220px)] overflow-auto">
              {packs.map((pack) => (
                <button
                  key={pack.id}
                  type="button"
                  onClick={() => setSelectedPackId(pack.id)}
                  className={`w-full text-left px-4 py-3 border-b border-line last:border-0 transition-colors ${
                    selectedPackId === pack.id ? 'bg-accent/10' : 'hover:bg-panel2/70'
                  }`}
                >
                  <div className="flex gap-3">
                    {pack.coverImageUrl ? (
                      <img src={pack.coverImageUrl} alt="" className="w-16 h-12 rounded object-cover bg-panel2" />
                    ) : (
                      <div className="w-16 h-12 rounded bg-panel2 flex items-center justify-center text-[10px] text-text-faint">
                        No cover
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-text truncate">{pack.title}</span>
                        <span className={`shrink-0 px-1.5 py-0.5 text-[10px] rounded font-medium uppercase ${
                          pack.status === 'ready' ? 'bg-ok/20 text-ok' : 'bg-text-faint/20 text-text-dim'
                        }`}>
                          {pack.status}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-text-dim truncate">
                        {pack.sellerProfile.displayName || 'Unknown model'} | {pack.room.name}
                      </div>
                      <div className="mt-2 flex items-center gap-3 text-[11px] text-text-dim">
                        <span>{pack.imageCount} images</span>
                        <span>{pack.videoCount} videos</span>
                        <span>{formatMoney(pack.priceCents, pack.currency)}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="min-w-0">
            {selectedPack && (
              <div className="bg-panel rounded-xl border border-line overflow-hidden">
                <div className="p-5 border-b border-line">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h2 className="text-xl font-display font-medium text-text">{selectedPack.title}</h2>
                      <div className="mt-1 text-sm text-text-dim">
                        {selectedPack.sellerProfile.displayName || 'Unknown model'} | {selectedPack.room.name}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={`/s/${selectedPack.room.code}/summary`} className="btn btn-sm">
                        Sales page
                      </Link>
                      <Link to={`/s/${selectedPack.room.code}/packs`} className="btn btn-sm">
                        Edit pack
                      </Link>
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-px bg-line border border-line">
                    <PackMetric label="Public price" value={formatMoney(selectedPack.priceCents, selectedPack.currency)} />
                    <PackMetric label="Attendee price" value={formatMoney(selectedPack.attendeePriceCents, selectedPack.currency)} />
                    <PackMetric label="Purchases" value={selectedPack.completedPurchaseCount} />
                    <PackMetric label="Revenue" value={formatMoney(selectedPack.totalRevenueCents, selectedPack.currency)} />
                    <PackMetric label="Visibility" value={selectedPack.productVisibility.replaceAll('_', ' ')} />
                    <PackMetric label="Access" value={selectedPack.accessType} />
                    <PackMetric label="License" value={getLicenseLabel(selectedPack.license)} />
                    <PackMetric label="Expires" value={formatDate(selectedPack.accessExpiresOn)} />
                  </div>

                  {selectedPack.description && (
                    <p className="mt-4 text-sm leading-6 text-text-dim">{selectedPack.description}</p>
                  )}
                </div>

                {isDetailLoading ? (
                  <div className="p-8 text-center text-text-dim">Loading pack contents...</div>
                ) : detail ? (
                  <div className="p-5 space-y-8">
                    <section>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-text">Videos</h3>
                        <span className="text-xs text-text-dim">{detail.recordings.length} total</span>
                      </div>
                      {detail.recordings.length === 0 ? (
                        <div className="border border-line rounded-lg px-4 py-6 text-center text-sm text-text-dim">
                          No videos in this pack
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
                          {detail.recordings.map((recording) => (
                            <div key={recording.id} className="border border-line rounded-lg overflow-hidden bg-white">
                              <SecureVideoPlayer
                                recordingId={recording.id}
                                hasVideo={recording.hasVideo}
                                contentType={recording.contentType}
                                status={recording.status}
                                durationSeconds={recording.durationSeconds}
                                startedAt={recording.startedAt}
                              />
                              <div className="px-3 py-2 border-t border-line text-xs text-text-dim">
                                {recording.releaseStatus} | {formatFileSize(recording.fileSizeBytes) || 'size unknown'}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>

                    <section>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-text">Images</h3>
                        <span className="text-xs text-text-dim">{detail.assets.length} total</span>
                      </div>
                      {detail.assets.length === 0 ? (
                        <div className="border border-line rounded-lg px-4 py-6 text-center text-sm text-text-dim">
                          No images in this pack
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-3">
                          {detail.assets.map((asset) => (
                            <a
                              key={asset.id}
                              href={asset.imageUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="group border border-line rounded-lg overflow-hidden bg-white"
                            >
                              <div className="aspect-[4/3] bg-panel2 overflow-hidden">
                                <img
                                  src={getReferencePackThumbnailUrl(asset)}
                                  alt={getPoseLabel(asset)}
                                  className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
                                  loading="lazy"
                                />
                              </div>
                              <div className="p-2">
                                <div className="text-xs font-medium text-text truncate">{getPoseLabel(asset)}</div>
                                <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-text-dim">
                                  <span>{asset.curationStatus}</span>
                                  <span>{asset.width && asset.height ? `${asset.width}x${asset.height}` : formatFileSize(asset.fileSizeBytes)}</span>
                                </div>
                                <div className="mt-1 text-[11px] text-text-faint">
                                  {formatShortDateTime(asset.capturedAt)}
                                </div>
                              </div>
                            </a>
                          ))}
                        </div>
                      )}
                    </section>
                  </div>
                ) : (
                  <div className="p-8 text-center text-text-dim">Select another pack to retry loading details</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-text-dim">
            Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => current - 1)}
              disabled={page === 0}
              className="btn btn-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={page >= totalPages - 1}
              className="btn btn-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PackMetric({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="bg-panel px-3 py-2 min-h-[68px]">
      <div className="text-[10px] font-mono uppercase text-text-faint">{label}</div>
      <div className="mt-1 text-sm text-text">{value}</div>
    </div>
  );
}
